import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  IconPhone,
  IconPlus,
  IconPencil,
  IconCalendarOff,
  IconUserOff,
  IconUserCheck,
  IconClock,
  IconWorldOff,
} from "@tabler/icons-react";
import "./dentists.scss";
import { leaveOn, useClinicData } from "../../context/ClinicDataContext";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import ActionMenu from "../../components/ActionMenu/ActionMenu";
import DentistAvatar from "../../components/DentistAvatar/DentistAvatar";
import DentistFormModal, { WEEK } from "./DentistFormModal";
import LeaveModal, { formatLeaveRange } from "./LeaveModal";
import DeactivateModal from "./DeactivateModal";
import { formatTime } from "../../utils/format";
import { formatPhone, telHref } from "../../utils/phone";

const DentistsPage = () => {
  const { dentists, appointments, today, clinic, patientById, setDentistActive } = useClinicData();
  const [view, setView] = useState("active");
  const [editing, setEditing] = useState(null); // dentist, or {} for a new one
  const [leaveFor, setLeaveFor] = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  const active = dentists.filter((d) => d.active);
  const inactive = dentists.filter((d) => !d.active);
  const shown = view === "active" ? active : inactive;

  return (
    <div className="dentists-page">
      <div className="toolbar">
        <div className="tabs" role="tablist" aria-label="Dentist status">
          {[
            { id: "active", label: "Active", count: active.length },
            { id: "inactive", label: "Inactive", count: inactive.length },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={view === t.id}
              className={`tabs__item ${view === t.id ? "tabs__item--active" : ""}`}
              onClick={() => setView(t.id)}
            >
              {t.label}
              <span className="tabs__count">{t.count}</span>
            </button>
          ))}
        </div>
        <button className="btn btn--primary" type="button" onClick={() => setEditing({})}>
          <IconPlus size={18} stroke={2} />
          Add dentist
        </button>
      </div>

      {shown.length === 0 && (
        <div className="panel empty-state">
          <p className="empty-state__title">{view === "active" ? "No active dentists" : "No inactive dentists"}</p>
          <p>{view === "active" ? "Add a dentist so patients can start booking." : "Dentists you deactivate appear here."}</p>
        </div>
      )}

      <div className="dentists-page__grid">
        {shown.map((d) => {
          const todays = appointments
            .filter((a) => a.dentistId === d.id && a.date === today && a.status !== "cancelled" && a.status !== "no-show")
            .sort((x, y) => x.start.localeCompare(y.start));
          const upcomingCount = appointments.filter(
            (a) => a.dentistId === d.id && a.date > today && a.status !== "cancelled"
          ).length;
          const onLeaveToday = leaveOn(d, today);
          const nextLeave = d.leave.find((l) => l.from > today);

          const menu = d.active
            ? [
                { label: "Edit profile", icon: IconPencil, onSelect: () => setEditing(d) },
                { label: "Schedule & leave", icon: IconCalendarOff, onSelect: () => setLeaveFor(d) },
                { label: "Deactivate", icon: IconUserOff, tone: "danger", onSelect: () => setDeactivating(d) },
              ]
            : [
                { label: "Edit profile", icon: IconPencil, onSelect: () => setEditing(d) },
                { label: "Reactivate", icon: IconUserCheck, onSelect: () => setDentistActive(d.id, true) },
              ];

          return (
            <article
              className={`dentist-card ${d.active ? "" : "dentist-card--inactive"}`}
              key={d.id}
              style={{ "--dentist-color": d.color }}
            >
              <header className="dentist-card__header">
                <DentistAvatar dentist={d} size="lg" />
                <div className="dentist-card__identity">
                  <h2 className="dentist-card__name">{d.name}</h2>
                  <p className="dentist-card__role">
                    {d.role}
                    {d.qualifications ? ` · ${d.qualifications}` : ""}
                  </p>
                </div>
                <ActionMenu items={menu} label={`Actions for ${d.name}`} />
              </header>

              {(onLeaveToday || !d.online || !d.active) && (
                <div className="dentist-card__flags">
                  {!d.active && <span className="badge badge--neutral">Inactive</span>}
                  {d.active && onLeaveToday && <span className="badge badge--warning">On leave today</span>}
                  {d.active && !d.online && (
                    <span className="badge badge--neutral">
                      <IconWorldOff size={12} stroke={2} /> Front desk only
                    </span>
                  )}
                </div>
              )}

              {d.focus.length > 0 && (
                <div className="dentist-card__focus">
                  {d.focus.map((f) => (
                    <span className="badge badge--neutral" key={f}>
                      {f}
                    </span>
                  ))}
                </div>
              )}

              <div className="dentist-card__section">
                <h3 className="dentist-card__label">Working days</h3>
                <ul className="dentist-card__week">
                  {WEEK.map((day) => {
                    const closed = !clinic.openDays.includes(day);
                    const on = d.days.includes(day);
                    return (
                      <li
                        key={day}
                        className={`dentist-card__day ${on ? "dentist-card__day--on" : ""} ${closed ? "dentist-card__day--closed" : ""}`}
                        aria-label={`${day}: ${closed ? "clinic closed" : on ? "working" : "off"}`}
                      >
                        {day.charAt(0)}
                      </li>
                    );
                  })}
                </ul>
                <p className="dentist-card__hours">
                  <IconClock size={14} stroke={1.75} />
                  {formatTime(d.start)} – {formatTime(d.end)}
                  {d.breakStart && ` · Break ${formatTime(d.breakStart)} – ${formatTime(d.breakEnd)}`}
                </p>
                {nextLeave && (
                  <p className="dentist-card__hours">
                    <IconCalendarOff size={14} stroke={1.75} />
                    {nextLeave.type}: {formatLeaveRange(nextLeave)}
                  </p>
                )}
              </div>

              {d.active && (
                <div className="dentist-card__section">
                  <h3 className="dentist-card__label">Today</h3>
                  {todays.length ? (
                    <ul className="dentist-card__today">
                      {todays.map((a) => (
                        <li key={a.id}>
                          <span>{formatTime(a.start)}</span>
                          <Link to={`${BASE_PATH}/patients/${a.patientId}`}>{patientById[a.patientId]?.name}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="dentist-card__muted">{onLeaveToday ? "On leave." : "No appointments today."}</p>
                  )}
                </div>
              )}

              <footer className="dentist-card__footer">
                {d.active ? (
                  <span>{upcomingCount} upcoming bookings</span>
                ) : (
                  <button className="btn btn--secondary btn--sm" type="button" onClick={() => setDentistActive(d.id, true)}>
                    <IconUserCheck size={16} stroke={1.75} />
                    Reactivate
                  </button>
                )}
                <a className="btn btn--secondary btn--sm" href={telHref(d.phone)}>
                  <IconPhone size={16} stroke={1.75} />
                  {formatPhone(d.phone)}
                </a>
              </footer>
            </article>
          );
        })}
      </div>

      {editing && <DentistFormModal dentist={editing.id ? editing : null} onClose={() => setEditing(null)} />}
      {leaveFor && (
        <LeaveModal dentist={dentists.find((d) => d.id === leaveFor.id)} onClose={() => setLeaveFor(null)} />
      )}
      {deactivating && <DeactivateModal dentist={deactivating} onClose={() => setDeactivating(null)} />}
    </div>
  );
};

export default DentistsPage;
